import React from 'react'
import ReactDOM from 'react-dom'
import './index.css'
import App from './App'
import reportWebVitals from './reportWebVitals'
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/browser'
// import '@aws-amplify/ui-react/styles.css'

import { ampli, Environment } from './ampli'
import markerSDK from '@marker.io/browser'
import { ZendeskSettings, ZendeskWidget } from './zendesk-types'
import CobrowseIO from 'cobrowse-sdk-js'

declare global {
  interface Window {
    zESettings?: ZendeskSettings
    zE?: ZendeskWidget
  }
}
if (process.env.NODE_ENV === 'production') {
  markerSDK
    .loadWidget({
      project: '6452b6a0223633bc6463e721',
      ssr: {
        renderDelay: '5000', // 0 - 15000 (ms)
      },
      silent: true,
    })
    .then((widget) => {
      if (process.env.REACT_APP_ENV !== 'prod') {
        widget.show()
      } else {
        widget.hide()
      }
      return widget
    })
}

ampli.load({
  environment: (process.env.REACT_APP_AMPLI_ENVIRONMENT || 'development') as Environment,
  client: {
    configuration: { flushQueueSize: 1, flushIntervalMillis: 10 }, // Send out event within 1 second instead of default 10 seconds.
  },
})

if (process.env.NODE_ENV === 'production') {
  const sentryProps: Sentry.BrowserOptions = {
    dsn: 'https://b0bc65fe14a24b829828fc08ee5a5677@o4504536900894720.ingest.sentry.io/4504578052718592',
    integrations: [new BrowserTracing(), new Sentry.Replay()],
    environment: process.env.REACT_APP_ENV || 'development',

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
  }
  if (process.env.REACT_APP_SENTRY_RELEASE) {
    sentryProps.release = process.env.REACT_APP_SENTRY_RELEASE
  }
  Sentry.init(sentryProps)
}

if (process.env.NODE_ENV === 'production') {
  CobrowseIO.license = 'Pqq8YE-hkWcCiw'
  CobrowseIO.start()
}

// Enhanced ResizeObserver error suppression
const isResizeObserverError = (message: unknown): boolean => {
  if (typeof message === 'string') {
    return (
      message.includes('ResizeObserver loop completed') ||
      message.includes('ResizeObserver loop limit exceeded') ||
      message.includes('undelivered notifications')
    )
  }
  return false
}

// Suppress ResizeObserver loop completed warnings (common React issue)
const resizeObserverErrorHandler = (error: ErrorEvent) => {
  if (isResizeObserverError(error.message)) {
    error.stopImmediatePropagation()
    error.preventDefault()
    return false
  }
  return true
}

// Handle unhandled promise rejections for ResizeObserver
const resizeObserverPromiseHandler = (event: PromiseRejectionEvent) => {
  if (event.reason && isResizeObserverError(event.reason.message)) {
    event.preventDefault()
    return false
  }
  return true
}

// Intercept and suppress ResizeObserver console errors
const originalConsoleError = console.error
const originalConsoleWarn = console.warn
console.error = (...args) => {
  if (args.some((arg) => isResizeObserverError(arg))) {
    return // Suppress ResizeObserver errors
  }
  originalConsoleError.apply(console, args)
}
console.warn = (...args) => {
  if (args.some((arg) => isResizeObserverError(arg))) {
    return // Suppress ResizeObserver warnings
  }
  originalConsoleWarn.apply(console, args)
}

// Enhanced global error handling with better pattern matching
const originalOnError = window.onerror
window.onerror = (message, source, lineno, colno, error) => {
  if (isResizeObserverError(message)) {
    return true // Suppress the error
  }
  return originalOnError ? originalOnError(message, source, lineno, colno, error) : false
}

// Handle modern error events
const originalOnUnhandledRejection = window.onunhandledrejection
window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  if (event.reason && isResizeObserverError(event.reason.message)) {
    event.preventDefault()
    return false
  }
  return originalOnUnhandledRejection ? originalOnUnhandledRejection.call(window, event) : true
}

if (typeof window !== 'undefined') {
  // Enhanced ResizeObserver override to prevent loop errors
  const OriginalResizeObserver = window.ResizeObserver
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      const wrappedCallback: ResizeObserverCallback = (entries, observer) => {
        // Use requestAnimationFrame to defer callback execution and prevent loops
        window.requestAnimationFrame(() => {
          try {
            callback(entries, observer)
          } catch (error) {
            if (error instanceof Error && isResizeObserverError(error.message)) {
              // Silently ignore ResizeObserver loop errors
              return
            }
            // Re-throw other errors
            console.error('ResizeObserver callback error:', error)
          }
        })
      }
      super(wrappedCallback)
    }
  }

  // Add event listeners for comprehensive error handling
  window.addEventListener('error', resizeObserverErrorHandler, true)
  window.addEventListener('unhandledrejection', resizeObserverPromiseHandler, true)

  // Additional safety net for uncaught exceptions
  window.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent) => {
      if (event.reason && typeof event.reason === 'object' && event.reason.stack) {
        if (isResizeObserverError(event.reason.stack)) {
          event.preventDefault()
          return false
        }
      }
      return true
    },
    true
  )
}

ReactDOM.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
  document.getElementById('root')
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
